// src/components/character/creation/steps/AbilityScores.js
import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Dices, RefreshCw, Info, ArrowUp, ArrowDown, 
  Heart, Shield, Zap, Brain, Eye, Speech
} from 'lucide-react';
import { calculateAttributeModifier as calculateModifier } from '../../../../game-system/core/types/attributes';

const ABILITY_ICONS = {
  strength: Zap,
  dexterity: Shield,
  constitution: Heart,
  intelligence: Brain,
  wisdom: Eye,
  charisma: Speech
};

const ABILITY_DESCRIPTIONS = {
  strength: {
    name: "Strength",
    description: "Raw physical power and athletic ability",
    affects: ["Melee attacks", "Athletic checks", "Carrying capacity"]
  },
  dexterity: {
    name: "Dexterity",
    description: "Agility, reflexes, and balance",
    affects: ["Armor Class", "Initiative", "Ranged attacks"]
  },
  constitution: {
    name: "Constitution",
    description: "Health, stamina, and vital force",
    affects: ["Hit Points", "Fortitude", "Endurance checks"]
  },
  intelligence: {
    name: "Intelligence",
    description: "Mental acuity, knowledge, and reasoning",
    affects: ["Spell power (Wizards)", "Knowledge checks", "Languages"]
  },
  wisdom: {
    name: "Wisdom",
    description: "Awareness, intuition, and insight",
    affects: ["Spell power (Clerics)", "Perception", "Survival"]
  },
  charisma: {
    name: "Charisma",
    description: "Force of personality and leadership",
    affects: ["Spell power (Sorcerers)", "Social checks", "Leadership"]
  }
};

const GENERATION_METHODS = {
  standard: {
    name: "Standard Array",
    description: "Use the standard set of scores: 15, 14, 13, 12, 10, 8",
    values: [15, 14, 13, 12, 10, 8]
  },
  roll: {
    name: "Roll Dice",
    description: "Roll dice one at a time and assign them to abilities",
    animation: true
  },
  pointBuy: {
    name: "Point Buy",
    description: "Spend points to build your ability scores",
    pointsTotal: 27
  }
};

export const AbilityScores = ({ character, onUpdate, onError, onSuccess }) => {
  const [isRolling, setIsRolling] = useState(false);
  const [generationMethod, setGenerationMethod] = useState('standard');
  const [pointBuyPoints, setPointBuyPoints] = useState(27);
  const [rollingAbility, setRollingAbility] = useState(null);
  
  // New state for individual dice rolling
  const [currentRoll, setCurrentRoll] = useState(null);
  const [rolledScores, setRolledScores] = useState([]);
  const [assignedAbilities, setAssignedAbilities] = useState({});

  // Calculate recommended abilities based on character attributes
  const getRecommendedAbilities = useCallback(() => {
    // Since we're using a skill-based system now, we recommend abilities
    // based on the background or just general recommendations
    if (character.background === 'scholar') {
      return ['intelligence', 'wisdom'];
    } else if (character.background === 'soldier') {
      return ['strength', 'constitution'];
    } else if (character.background === 'merchant') {
      return ['charisma', 'intelligence'];
    } else if (character.background === 'criminal') {
      return ['dexterity', 'charisma'];
    } else if (character.background === 'healer') {
      return ['wisdom', 'constitution'];
    } else if (character.background === 'artisan') {
      return ['dexterity', 'intelligence'];
    } else if (character.background === 'explorer') {
      return ['constitution', 'wisdom'];
    } else if (character.background === 'noble') {
      return ['charisma', 'intelligence'];
    } else {
      // Default recommendations if no background is selected
      return ['strength', 'dexterity'];
    }
  }, [character.background]);

  // Calculate point buy cost for a score
  const getPointBuyCost = (score) => {
    const costs = {
      8: 0, 9: 1, 10: 2, 11: 3, 12: 4,
      13: 5, 14: 7, 15: 9
    };
    return costs[score] || 0;
  };

  // Roll a single ability score (4d6 drop lowest)
  const rollSingleDice = useCallback(async () => {
    setIsRolling(true);
    
    // Simulate rolling animation
    for (let i = 0; i < 10; i++) {
      const tempRoll = Math.floor(Math.random() * 16) + 3;
      setCurrentRoll(tempRoll);
      await new Promise(resolve => setTimeout(resolve, 50));
    }

    // Final roll
    const rolls = Array(4).fill(0).map(() => Math.floor(Math.random() * 6) + 1);
    rolls.sort((a, b) => b - a);
    const finalScore = rolls.slice(0, 3).reduce((sum, roll) => sum + roll, 0);

    setCurrentRoll(finalScore);
    setRolledScores(prev => [...prev, finalScore]);
    setIsRolling(false);
    
    onSuccess(`You rolled a ${finalScore}!`);
  }, [onSuccess]);

  // Assign roll to ability
  const assignRoll = useCallback((ability, rollIndex) => {
    if (rollIndex === undefined || rollIndex < 0 || rollIndex >= rolledScores.length) {
      return;
    }

    const roll = rolledScores[rollIndex];
    
    // Update assigned abilities tracker
    setAssignedAbilities(prev => ({
      ...prev,
      [ability]: rollIndex
    }));

    // Update character abilities
    onUpdate({
      abilities: {
        ...character.abilities,
        [ability]: roll
      }
    });
    
    onSuccess(`Assigned ${roll} to ${ABILITY_DESCRIPTIONS[ability].name}`);
  }, [character.abilities, onUpdate, rolledScores, onSuccess]);

  // Unassign roll from ability
  const unassignAbility = useCallback((ability) => {
    if (!assignedAbilities[ability]) {
      return;
    }

    // Remove ability from assigned list
    const newAssignedAbilities = { ...assignedAbilities };
    delete newAssignedAbilities[ability];
    setAssignedAbilities(newAssignedAbilities);

    // Reset ability score to default
    onUpdate({
      abilities: {
        ...character.abilities,
        [ability]: 10 // Reset to default
      }
    });
    
    onSuccess(`Unassigned ${ABILITY_DESCRIPTIONS[ability].name}`);
  }, [assignedAbilities, character.abilities, onUpdate, onSuccess]);

  // Reset all rolls
  const resetRolls = useCallback(() => {
    setRolledScores([]);
    setAssignedAbilities({});
    setCurrentRoll(null);
    
    // Reset all abilities to default
    const defaultAbilities = Object.keys(character.abilities).reduce((acc, ability) => {
      acc[ability] = 10;
      return acc;
    }, {});
    
    onUpdate({
      abilities: defaultAbilities
    });
    
    onSuccess('All rolls have been reset');
  }, [character.abilities, onUpdate, onSuccess]);

  // Legacy function - now modified to use standard array
  const handleRollAll = async () => {
    // For standard array, distribute the values according to background-based recommendations
    const standardArray = GENERATION_METHODS.standard.values.slice();
    const abilities = Object.keys(character.abilities);
    const recommended = getRecommendedAbilities();
    
    // Sort abilities to put recommended ones first
    abilities.sort((a, b) => {
      if (recommended.includes(a) && !recommended.includes(b)) return -1;
      if (!recommended.includes(a) && recommended.includes(b)) return 1;
      if (recommended.includes(a) && recommended.includes(b)) {
        return recommended.indexOf(a) - recommended.indexOf(b);
      }
      return 0;
    });
    
    // Assign values from standard array
    const newAbilities = {};
    abilities.forEach((ability, index) => {
      newAbilities[ability] = standardArray[index];
    });
    
    onUpdate({ abilities: newAbilities });
    onSuccess('Standard array has been applied!');
  };

  // Handle point buy
  const handlePointBuyChange = (ability, newValue) => {
    const oldCost = getPointBuyCost(character.abilities[ability]);
    const newCost = getPointBuyCost(newValue);
    const pointDiff = newCost - oldCost;

    if (pointBuyPoints - pointDiff < 0) {
      onError('Not enough points remaining');
      return;
    }

    setPointBuyPoints(prev => prev - pointDiff);
    onUpdate({
      abilities: {
        ...character.abilities,
        [ability]: newValue
      }
    });
  };

  // Handle method change
  const handleMethodChange = (method) => {
    if (method === generationMethod) return;
    
    setGenerationMethod(method);
    resetRolls();
    
    if (method === 'pointBuy') {
      setPointBuyPoints(27);
      // Reset to minimum values
      onUpdate({
        abilities: {
          strength: 8,
          dexterity: 8,
          constitution: 8,
          intelligence: 8,
          wisdom: 8,
          charisma: 8
        }
      });
    } else if (method === 'standard') {
      handleRollAll(); // Apply standard array
    }
  };

  // Ability Score Component
  const AbilityScore = ({ ability }) => {
    const Icon = ABILITY_ICONS[ability];
    const recommendedAbilities = getRecommendedAbilities();
    const isRecommended = recommendedAbilities.includes(ability);
    const score = character.abilities[ability];
    const modifier = calculateModifier(score);
    const isAssigned = ability in assignedAbilities;

    return (
      <motion.div
        className={`character-creation__ability 
          ${isRecommended ? 'character-creation__ability--recommended' : ''} 
          ${isAssigned ? 'character-creation__ability--assigned' : ''}
          ${generationMethod === 'roll' && !isAssigned ? 'character-creation__ability--unassigned' : ''}`}
        whileHover={{ scale: 1.05 }}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div className="character-creation__ability-header">
          <Icon size={24} />
          <h3 className="character-creation__ability-name">
            {ABILITY_DESCRIPTIONS[ability].name}
          </h3>
          {isRecommended && (
            <span className="character-creation__ability-recommended-tag">
              Recommended
            </span>
          )}
        </div>

        <div className="character-creation__ability-score">
          {rollingAbility === ability ? (
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 0.5, repeat: Infinity }}
            >
              <Dices size={32} />
            </motion.div>
          ) : (
            <>
              <span className="character-creation__score-value">{score}</span>
              <span className="character-creation__score-modifier">
                {modifier >= 0 ? `+${modifier}` : modifier}
              </span>
            </>
          )}
        </div>

        {generationMethod === 'pointBuy' && (
          <div className="character-creation__point-buy-controls">
            <button
              className="character-creation__point-buy-button"
              onClick={() => handlePointBuyChange(ability, score - 1)}
              disabled={score <= 8}
            >
              <ArrowDown size={16} />
            </button>
            <button
              className="character-creation__point-buy-button"
              onClick={() => handlePointBuyChange(ability, score + 1)}
              disabled={score >= 15 || pointBuyPoints < getPointBuyCost(score + 1) - getPointBuyCost(score)}
            >
              <ArrowUp size={16} />
            </button>
          </div>
        )}

        {generationMethod === 'roll' && (
          <div className="character-creation__ability-assignment">
            {isAssigned ? (
              <button
                className="character-creation__unassign-button"
                onClick={() => unassignAbility(ability)}
              >
                Unassign
              </button>
            ) : (
              <div className="character-creation__roll-assignment">
                {rolledScores.length > 0 ? (
                  <div className="character-creation__roll-dropdown">
                    <select
                      className="character-creation__roll-select"
                      onChange={(e) => assignRoll(ability, parseInt(e.target.value))}
                      value=""
                    >
                      <option value="" disabled>Assign a roll...</option>
                      {rolledScores.map((roll, index) => {
                        // Skip if already assigned
                        if (Object.values(assignedAbilities).includes(index)) return null;
                        return (
                          <option key={index} value={index}>
                            Roll {index + 1}: {roll}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                ) : (
                  <div className="character-creation__no-rolls">
                    Roll dice to assign
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="character-creation__ability-affects">
          {ABILITY_DESCRIPTIONS[ability].affects.map((effect, index) => (
            <span key={index} className="character-creation__ability-tag">
              {effect}
            </span>
          ))}
        </div>
      </motion.div>
    );
  };

  // Determine if all abilities have been assigned
  const allAbilitiesAssigned = generationMethod === 'roll' && 
    Object.keys(character.abilities).length === Object.keys(assignedAbilities).length;

  return (
    <motion.div
      className="character-creation__content"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
    >
      {/* Generation Method Selection */}
      <div className="character-creation__method-selection">
        {Object.entries(GENERATION_METHODS).map(([method, details]) => (
          <button
            key={method}
            className={`character-creation__method-button ${
              generationMethod === method ? 'character-creation__method-button--active' : ''
            }`}
            onClick={() => handleMethodChange(method)}
          >
            <h3>{details.name}</h3>
            <p>{details.description}</p>
          </button>
        ))}
      </div>

      {/* Point Buy Points Display */}
      {generationMethod === 'pointBuy' && (
        <div className="character-creation__points-display">
          <h3>Points Remaining: {pointBuyPoints}</h3>
          <div className="character-creation__points-bar">
            <div 
              className="character-creation__points-progress"
              style={{ width: `${(pointBuyPoints / 27) * 100}%` }}
            />
          </div>
        </div>
      )}

      {/* Dice Rolling Interface */}
      {generationMethod === 'roll' && (
        <div className="character-creation__roll-interface">
          <div className="character-creation__roll-status">
            <h3 className="character-creation__roll-heading">
              Roll Your Dice
            </h3>
            
            <div className="character-creation__roll-tracker">
              <span>Rolls: {rolledScores.length} / 6</span>
              <span>Assigned: {Object.keys(assignedAbilities).length} / 6</span>
            </div>
            
            <div className="character-creation__current-roll">
              {isRolling ? (
                <div className="character-creation__rolling-animation">
                  <Dices className="spin-animation" size={32} />
                  <span>Rolling...</span>
                </div>
              ) : currentRoll ? (
                <>
                  <span className="character-creation__roll-result">
                    You rolled: <strong>{currentRoll}</strong>
                  </span>
                  <span className="character-creation__roll-instruction">
                    Assign this roll to an ability
                  </span>
                </>
              ) : (
                <span className="character-creation__roll-instruction">
                  Click Roll Dice to begin
                </span>
              )}
            </div>
            
            <div className="character-creation__roll-history">
              {rolledScores.map((score, index) => {
                // Check if this roll is assigned
                const isAssigned = Object.values(assignedAbilities).includes(index);
                const assignedTo = Object.entries(assignedAbilities)
                  .find(([_, rollIndex]) => rollIndex === index)?.[0];
                
                return (
                  <div 
                    key={index} 
                    className={`character-creation__roll-history-item ${isAssigned ? 'character-creation__roll-history-item--assigned' : ''}`}
                  >
                    <span>Roll {index + 1}: {score}</span>
                    {isAssigned && assignedTo && (
                      <span className="character-creation__roll-assigned-to">
                        → {ABILITY_DESCRIPTIONS[assignedTo].name}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          
          <div className="character-creation__roll-actions">
            <button
              className="character-creation__button"
              onClick={rollSingleDice}
              disabled={isRolling || rolledScores.length >= 6}
            >
              <Dices />
              Roll Dice {rolledScores.length > 0 ? `(${rolledScores.length}/6)` : ''}
            </button>
            
            <button
              className="character-creation__button character-creation__button--secondary"
              onClick={resetRolls}
              disabled={rolledScores.length === 0}
            >
              <RefreshCw />
              Reset Rolls
            </button>
          </div>
          
          {allAbilitiesAssigned && (
            <div className="character-creation__success-message">
              <div className="character-creation__success-icon">✓</div>
              <p>All abilities have been assigned successfully!</p>
            </div>
          )}
        </div>
      )}

      {/* Ability Scores Grid */}
      <div className="character-creation__abilities">
        {Object.keys(character.abilities).map(ability => (
          <AbilityScore key={ability} ability={ability} />
        ))}
      </div>

      {/* Background Recommendations */}
      {character.background && (
        <div className="character-creation__help-text">
          <Info size={20} />
          <p>
            For a {character.background}, focus on{' '}
            {getRecommendedAbilities().join(' and ')}.
            These abilities will be crucial for your character's effectiveness.
          </p>
        </div>
      )}

      {/* Racial Bonuses Display */}
      {character.race && (
        <motion.div
          className="character-creation__racial-bonuses"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h3>Racial Ability Bonuses</h3>
          <div className="character-creation__bonus-grid">
            {Object.entries(character.abilities).map(([ability, score]) => {
              const racialBonus = 0; // Calculate from race
              if (racialBonus) {
                return (
                  <div key={ability} className="character-creation__bonus">
                    <span>{ABILITY_DESCRIPTIONS[ability].name}</span>
                    <span className="character-creation__bonus-value">
                      +{racialBonus}
                    </span>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default AbilityScores;